// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { addToChainBefore, Handler, replaceInChain } from "@insite/client-framework/HandlerCreator";
import {
    chain,
    CopyCurrentValues,
    SetConfigurationSelection,
} from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/UpdateConfigurationSelection";

interface Selection {
    configSectionId: string;
    sectionOptionId?: string;
}

// XNG-Changes: client-framework doesn't export its Parameter/Props types for this handler, so pull them back
// out via typeof/Parameters instead of hand-retyping them - keeps this file from silently drifting out of
// sync if the base handler's shape ever changes.
type BaseHandlerProps = Parameters<typeof CopyCurrentValues>[0];
type BaseParameter = BaseHandlerProps["parameter"];
type BaseProps = Omit<BaseHandlerProps, "parameter" | "dispatch" | "getState">;

// XNG-Changes: widen the base Parameter shape with an optional batch of selections.
type Parameter = BaseParameter & {
    /**
     * Extra selections to apply together with configSectionId/sectionOptionId (if provided) in this same
     * dispatch. Used to apply several selections atomically - e.g. auto-selecting every single-option
     * section of a bundle on mount - as one store update and one real-time pricing/inventory request,
     * instead of one dispatch (and one request) per selection.
     */
    additionalSelections?: Selection[];
};

type HandlerType = Handler<Parameter, BaseProps>;

const getAllSelections = (parameter: Parameter): Selection[] => [
    ...(parameter.configSectionId
        ? [{ configSectionId: parameter.configSectionId, sectionOptionId: parameter.sectionOptionId }]
        : []),
    ...(parameter.additionalSelections ?? []),
];

// XNG-Changes: bail out (skip the real-time pricing/inventory calls too) when none of the requested
// selections actually change the store. Widgets that re-apply default selections on every render (e.g. an
// effect that auto-selects a bundle's single-option parts) were re-dispatching, and re-requesting real-time
// pricing/inventory, with a selection that was already applied.
const XngSkipIfSelectionUnchanged: HandlerType = props => {
    const selections = getAllSelections(props.parameter);
    if (selections.length === 0) {
        return;
    }

    const currentSelection = props.getState().pages.productDetails.configurationSelection;
    const hasChange = selections.some(
        selection => currentSelection[selection.configSectionId] !== selection.sectionOptionId,
    );
    if (!hasChange) {
        return false;
    }
};

// XNG-Changes: also apply every selection in parameter.additionalSelections (if any), so a widget can batch
// several selections into a single dispatch/request instead of one dispatch (and one real-time
// pricing/inventory request) per selection.
const XngSetConfigurationSelection: HandlerType = props => {
    getAllSelections(props.parameter).forEach(selection => {
        props.configurationSelection[selection.configSectionId] = selection.sectionOptionId;
    });
};

addToChainBefore(chain, CopyCurrentValues, XngSkipIfSelectionUnchanged);
replaceInChain(chain, SetConfigurationSelection, XngSetConfigurationSelection);
